/**
 * Die Ansprüche eines OIDC-Tokens und die Form, in der dieses Paket antwortet.
 *
 * Alle Felder sind `unknown`, und das ist Absicht. Ein Token kommt von außen;
 * dass `exp` eine Zahl ist, ist eine Behauptung des Absenders, bis sie geprüft
 * wurde. Ein Typ, der das verschweigt, verlagert die Prüfung an jede
 * Aufrufstelle — und dort wird sie irgendwann vergessen.
 */

import type { ErrorEnvelope } from "@hwc/contracts";

export interface Anspruch {
  readonly [name: string]: unknown;
}

/**
 * Ergebnis einer Prüfung: entweder ein Wert oder ein Fehler nach `04` §4.6.
 *
 * Kein Werfen. Ein abgelaufenes oder gefälschtes Token ist ein erwartbarer
 * Eingabefall, kein Programmfehler — genauso wie ein ungültiger ResourceRef,
 * für den `parseResourceRef` `undefined` liefert statt zu werfen.
 */
export type Ergebnis<T> =
  | { readonly ok: true; readonly wert: T }
  | { readonly ok: false; readonly fehler: ErrorEnvelope };

export function gelungen<T>(wert: T): Ergebnis<T> {
  return { ok: true, wert };
}

export function gescheitert<T>(fehler: ErrorEnvelope): Ergebnis<T> {
  return { ok: false, fehler };
}

/** Liest einen Anspruch als nicht-leere Zeichenkette, sonst `undefined`. */
export function alsText(wert: unknown): string | undefined {
  return typeof wert === "string" && wert.length > 0 ? wert : undefined;
}

/**
 * Liest einen Anspruch als Zahl.
 *
 * Nur echte Zahlen, keine Zeichenketten: Ein `exp: "0"` würde sonst zu 0 und
 * damit zu einem immer abgelaufenen Token — oder, je nach Vergleich, zu einem
 * nie ablaufenden. Wer eine Zeitangabe als Text schickt, hält sich nicht an
 * RFC 7519, und das ist ein Grund zur Ablehnung, nicht zur Auslegung.
 */
export function alsZahl(wert: unknown): number | undefined {
  return typeof wert === "number" && Number.isFinite(wert) ? wert : undefined;
}

/**
 * Liest einen Anspruch als Liste von Zeichenketten.
 *
 * OIDC erlaubt für `aud` sowohl eine Zeichenkette als auch eine Liste
 * (RFC 7519 §4.1.3). Beides hier zusammenzuführen erspart die Fallunterscheidung
 * an jeder Prüfstelle.
 */
export function alsTextliste(wert: unknown): readonly string[] {
  if (typeof wert === "string") return wert.length > 0 ? [wert] : [];
  if (!Array.isArray(wert)) return [];
  return wert.filter((x): x is string => typeof x === "string" && x.length > 0);
}
